import React, { useState, useEffect } from 'react';
import {
    Modal, Box, SpaceBetween, Button, Input, Textarea,
    Multiselect, Alert, Spinner, ColumnLayout, Select
} from '@amzn/awsui-components-react';
import { useSelector } from 'react-redux';
import { executeQuery } from 'src/utilities/AppSyncPlusAPIs';
import appSettings from './app_settings';
import CustomMultiHybridInputAndSelect from '../Templates/components/CustomMultiHybridInputAndSelect';
import { PROCESS_KEYS, UPLOADER_TYPES } from '../Frisco/Constants';
import { useSuggestedLogins } from 'src/hooks/useSuggestedLogins';
import { loadWorkdocsUploadDefaults } from './WorkdocsUploadDefaultsForm';

const FILE_TYPE_OPTIONS = [
    { label: 'csv', value: 'csv' },
    { label: 'xlsx', value: 'xlsx' },
    { label: 'xls', value: 'xls' },
    { label: 'txt', value: 'txt' },
    { label: 'pdf', value: 'pdf' },
];

/**
 * CreateFriscoModal - A simplified form for SEC Central (Corp Reporting) admins
 * to create a new FRISCO process (Workdocs-only type) with pre-set configurations.
 * 
 * Mimics the field labels and structure of the FRISCO Create Process page
 * (AddOrEditProcessComponent.jsx).
 */
export default function CreateFriscoModal({ visible, onDismiss, onSuccess, workstreamOptions = [] }) {
    const currentStage = useSelector((globalState) => globalState.auth.currentStage);
    const userId = useSelector((globalState) => globalState.auth.userId);
    const appConfig = appSettings[currentStage] || {};
    const appId = appConfig.appId;
    const friscoWorkdocsFolderId = appConfig.friscoWorkdocsFolderId;

    const [processName, setProcessName] = useState('');
    const [label, setLabel] = useState('');
    const [description, setDescription] = useState('');
    const [fileTypes, setFileTypes] = useState([]);
    const [selectedWorkstream, setSelectedWorkstream] = useState(null);
    const [formData, setFormData] = useState({ [PROCESS_KEYS.uploaders]: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [uploadersKey, setUploadersKey] = useState(0);
    const [workdocsDefaults, setWorkdocsDefaults] = useState(null);
    const [defaultsLoading, setDefaultsLoading] = useState(true);

    const { suggestedLogins, aliasesLoading, getSuggestedLoginsCaller } = useSuggestedLogins();

    // Load workdocs upload defaults from the generic preference table
    useEffect(() => {
        if (appId && currentStage) {
            setDefaultsLoading(true);
            loadWorkdocsUploadDefaults(appId, currentStage)
                .then((defaults) => {
                    setWorkdocsDefaults(defaults);
                })
                .catch((e) => {
                    console.error('Failed to load workdocs upload defaults:', e);
                })
                .finally(() => setDefaultsLoading(false));
        } else {
            setDefaultsLoading(false);
        }
    }, [appId, currentStage]);

    const resetForm = () => {
        setProcessName('');
        setLabel('');
        setDescription('');
        setFileTypes([]);
        setSelectedWorkstream(null);
        setFormData({ [PROCESS_KEYS.uploaders]: [] });
        setUploadersKey(prev => prev + 1);
        setError(null);
        setSuccess(false);
    };

    const handleDismiss = () => {
        resetForm();
        onDismiss();
    };

    // Functions for CustomMultiHybridInputAndSelect compatibility
    const getValue = (fieldName) => {
        return formData?.[fieldName];
    };

    const updateValue = (fieldName, value) => {
        setFormData(prev => ({ ...prev, [fieldName]: value }));
    };

    const getSelectFieldValues = (fieldName) => {
        if (fieldName === PROCESS_KEYS.uploaders) {
            return [
                { label: "USER", value: UPLOADER_TYPES.UPLOADER },
                { label: "LDAP", value: UPLOADER_TYPES.UPLOADER_LDAP }
            ];
        }
        return [];
    };

    const postProcessTeamEmails = (value) => {
        return value.replaceAll("@amazon.com", "");
    };

    const approversValidation = (elements) => {
        let valid = true;
        elements?.forEach((element) => {
            if (!element.type || !element.alias) {
                valid = false;
            }
        });
        return valid;
    };

    const validateForm = () => {
        if (!appId || !friscoWorkdocsFolderId) return 'Application is not configured for this stage';
        if (!processName.trim()) return 'Process Name is required';
        if (!selectedWorkstream) return 'Workstream is required';
        if (fileTypes.length === 0) return 'At least one file type is required';
        // Validate uploader entries if any are added (but uploaders are not required)
        const uploadersList = formData[PROCESS_KEYS.uploaders] || [];
        for (const uploader of uploadersList) {
            if (!uploader.type || !uploader.alias) {
                return 'Each uploader must have both a type and an alias';
            }
        }
        return null;
    };

    const isFormValid = () => validateForm() === null;

    const handleSubmit = async () => {
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        setError(null);

        const variables = {
            input: {
                process_name: processName.trim(),
                app_id: String(appId),
                label: label.trim(),
                description: description.trim(),
                file_types: fileTypes.map(ft => ft.value),
                uploaders: (formData[PROCESS_KEYS.uploaders] || []).map(u => ({
                    alias: u.alias,
                    type: u.type
                })),
                created_by: userId,
                last_updated_by: userId,
                group_id: selectedWorkstream.value,
                next_step: "CORP_ADMIN_APPROVAL",
                environment: "Transfer_S3_Workdocs",
                approval_required: false,
                multipart_allowed: false,
                critical_data: false,
                sox_approved: false,
                custom_attributes: [
                    { attribute_type: "BACKUP_WORKDOCS_LOCATION_FOLDER_ID", attribute: workdocsDefaults?.workdocsFolderId || friscoWorkdocsFolderId, sub_type: "FRISCO" },
                    { attribute_type: "BACKUP_WORKDOCS_LOCATION", attribute: workdocsDefaults?.folderPathPattern || "Accounting Central Downloads/{previous_quarter} - {year}/{alias}", sub_type: "FRISCO" },
                    { attribute_type: "FILE_PREFIX", attribute: workdocsDefaults?.fileNameFormat || "[{process_name}][{process_label}][{wb_status}]{uploaded_file_name}", sub_type: "FRISCO" },
                    { attribute_type: "BACKUP_WORKDOCS_LOCATION_ENABLED", attribute: "true", sub_type: "FRISCO" },
                    { attribute_type: "NOTIFICATION_PREFERENCE", attribute: "DEFAULT", sub_type: "FRISCO" }
                ]
            }
        };

        const qry = `mutation CreateProcessForCorpReporting($input: ProcessInputV2) {
            createProcessForCorpReporting(input: $input) {
                process_id
                process_name
                label
                description
                app_id
                environment
                file_types
                status
            }
        }`;

        try {
            await new Promise((resolve, reject) => {
                executeQuery(qry, (response) => {
                    resolve(response);
                }, (err) => {
                    reject(err);
                }, variables);
            });
            setSuccess(true);
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error('Failed to create FRISCO process:', err);
            setError(err?.errors?.[0]?.message || err?.message || 'Failed to create FRISCO process. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            visible={visible}
            onDismiss={handleDismiss}
            header="Create New Deliverable"
            size="large"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        {loading && <Box><Spinner /> Saving...</Box>}
                        <Button variant="link" onClick={handleDismiss} disabled={loading}>Cancel</Button>
                        <Button variant="primary" onClick={handleSubmit} disabled={loading || success || defaultsLoading || !isFormValid()}>
                            Create
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween direction="vertical" size="l">
                {error && (
                    <Alert type="error" dismissible onDismiss={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert type="success">
                        Process created successfully! You can close this dialog.
                    </Alert>
                )}

                <ColumnLayout columns={2}>
                    <div>
                        <Box variant="h4">Process Name<Box variant="span" color="text-status-error"> *</Box></Box>
                        <Input
                            value={processName}
                            onChange={({ detail }) => setProcessName(detail.value)}
                            placeholder="Process name"
                            disabled={loading || success}
                        />
                    </div>
                    <div>
                        <Box variant="h4">Label - To display in the application page</Box>
                        <Input
                            value={label}
                            onChange={({ detail }) => setLabel(detail.value)}
                            placeholder="label"
                            disabled={loading || success}
                        />
                    </div>
                    <div>
                        <Box variant="h4">Description</Box>
                        <Textarea
                            value={description}
                            onChange={({ detail }) => setDescription(detail.value)}
                            placeholder="Description"
                            disabled={loading || success}
                            rows={3}
                        />
                    </div>
                </ColumnLayout>

                <ColumnLayout columns={2}>
                    <div>
                        <Box variant="h4">Load file type<Box variant="span" color="text-status-error"> *</Box></Box>
                        <Multiselect
                            selectedOptions={fileTypes}
                            onChange={({ detail }) => setFileTypes(detail.selectedOptions)}
                            options={FILE_TYPE_OPTIONS}
                            placeholder="Select file types"
                            disabled={loading || success}
                        />
                    </div>
                    <div>
                        <Box variant="h4">Workstream<Box variant="span" color="text-status-error"> *</Box></Box>
                        <Select
                            selectedOption={selectedWorkstream}
                            onChange={({ detail }) => setSelectedWorkstream(detail.selectedOption)}
                            options={workstreamOptions}
                            placeholder="Select a workstream"
                            disabled={loading || success}
                        />
                    </div>
                </ColumnLayout>

                <div>
                    <Box variant="h1">Uploader(s) Details</Box>
                    <CustomMultiHybridInputAndSelect
                        key={`uploaders-${uploadersKey}`}
                        field={PROCESS_KEYS.uploaders}
                        inputKey={PROCESS_KEYS.UPLOADER_KEYS.alias}
                        selectKey={PROCESS_KEYS.UPLOADER_KEYS.type}
                        selectHint="Choose type"
                        placeholder="alias"
                        getValues={getValue}
                        postProcess={postProcessTeamEmails}
                        validator={approversValidation}
                        updateValue={updateValue}
                        getOptions={getSelectFieldValues}
                        info=" "
                        errorMessage="type and alias input are mandatory"
                        duplicatesNotAllowed={true}
                        loginOptions={suggestedLogins}
                        getLoginOptions={getSuggestedLoginsCaller}
                        aliasesLoading={aliasesLoading}
                    />
                </div>

                <Box variant="small" color="text-body-secondary">
                    <strong>Pre-configured settings{defaultsLoading ? ' (loading...)' : ''}:</strong>
                    <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                        <li>Environment: File transfer to Workdocs/S3</li>
                        <li>Approval Required: No</li>
                        <li>Workdocs Folder ID: {workdocsDefaults?.workdocsFolderId ? `${workdocsDefaults.workdocsFolderId.substring(0, 16)}...` : 'SEC Central shared folder'}</li>
                        <li>Workdocs Subfolder: {workdocsDefaults?.folderPathPattern || 'Accounting Central Downloads/{previous_quarter} - {year}/{alias}'}</li>
                        <li>File Name Prefix: {workdocsDefaults?.fileNameFormat || '[{process_name}][{process_label}][{wb_status}]{uploaded_file_name}'}</li>
                        <li>Notifications: DEFAULT</li>
                    </ul>
                </Box>
            </SpaceBetween>
        </Modal>
    );
}
